import { BodyText, Button, IconSet } from '@postscript/components';
import React from 'react';
import styled from 'styled-components';

import { ROW_LEFT_INSET, SectionCard, SectionHeader } from '../components';
import { MESSAGE_INPUTS_UNAVAILABLE, SECTION_TITLES } from '../copy';

// Same id as MessageInputsSection — exactly one of the two renders at a time.
const SECTION_ID = 'message-details-message-inputs';

// Shares the row left inset so the copy lines up with row labels in the
// surrounding cards.
const PlaceholderBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) ${ROW_LEFT_INSET};
`;

interface MessageInputsPlaceholderSectionProps {
  // 'empty': the snapshot doesn't exist (404) — calm copy, no retry, since
  // older messages will never get one. 'error': we couldn't load it — offer
  // Retry.
  variant: 'empty' | 'error';
  onRetry?: () => void;
  // Admin-only contract editor entry, threaded from the drawer. Only shown on
  // the 'empty' variant.
  onOpenEditor?: () => void;
}

// Stand-in for MessageInputsSection when there's no details payload. The rest
// of the drawer (message bubble, feedback, subscriber card) renders normally
// from live props — only this card's data actually depends on the API.
export const MessageInputsPlaceholderSection: React.FC<MessageInputsPlaceholderSectionProps> =
  ({ variant, onRetry, onOpenEditor }) => (
    <SectionCard ariaLabelledBy={SECTION_ID} flush>
      <SectionHeader id={SECTION_ID} icon={IconSet.GenerateMessage} divider>
        {SECTION_TITLES.messageInputs}
      </SectionHeader>
      <PlaceholderBody>
        <BodyText size="small" color="var(--text-color-dim)">
          {variant === 'error'
            ? MESSAGE_INPUTS_UNAVAILABLE.error
            : MESSAGE_INPUTS_UNAVAILABLE.empty}
        </BodyText>
        {variant === 'error' && onRetry && (
          <Button variant="secondary" size="small" onClick={onRetry}>
            {MESSAGE_INPUTS_UNAVAILABLE.retry}
          </Button>
        )}
        {variant === 'empty' && onOpenEditor && (
          <Button variant="secondary" size="small" onClick={onOpenEditor}>
            {MESSAGE_INPUTS_UNAVAILABLE.openEditor}
          </Button>
        )}
      </PlaceholderBody>
    </SectionCard>
  );
